import _ from 'lodash';
import { strategyFor } from '@/builder/products';

export function emptyItem() {
  return {
    id: null,
    quoteId: null,
    product: null,
    productId: null,
    productLayout: null,
    productLayoutId: null,
    ref: null,
    label: null,
    width: null,
    height: null,
    specs: {},
    data: {},
    valid: null,
    validStep: 1,
    adjusters: null
  };
}

export function parseComponents(components) {
  if (_.isNil(components) || _.isObject(components)) return components ?? null;
  if (typeof components !== 'string' || !components.length) return null;
  try {
    return JSON.parse(components);
  } catch (e) {
    return null;
  }
}

export function findById(list, id) {
  if (!list || !_.isFunction(list.find)) return undefined;
  return list.find((obj) => obj.id === id);
}

export function selectedSectionConfigs(configOptions, sections) {
  const filteredOpts = [];
  if (!_.isUndefined(configOptions) && !_.isUndefined(sections)) {
    sections.forEach((section) => {
      filteredOpts.push(configOptions.find(({ id }) => id === section.configId));
    });
  }
  return filteredOpts;
}

export function itemBenchWidth(sections, configOptions) {
  let benchWidth = 0;
  if (!_.isUndefined(sections) && _.isArray(sections)) {
    sections.forEach((section) => {
      if (section.configId) {
        const config = findById(configOptions, section.configId);
        if (config) benchWidth += config.benchtopWidth;
      }
    });
  }
  return benchWidth;
}

export function itemDoorCount(productLayoutId, productLayouts, hingedWardrobeLayoutOptions) {
  let doorCount = 0;
  if (!_.isUndefined(productLayoutId) && !_.isNull(productLayoutId)) {
    let productLayout = null;
    if (!_.isUndefined(productLayouts)) {
      productLayout = findById(productLayouts, productLayoutId);
    }
    if (productLayout) doorCount = parseFloat(productLayout.slug.split('-')[1]);
  }

  if (!doorCount && !_.isUndefined(productLayouts)) {
    let doorCounts = [];
    hingedWardrobeLayoutOptions.forEach((section) => {
      doorCounts.push(parseFloat(section.slug.split('-')[1]));
    });
    doorCounts = [...new Set(doorCounts)];
    if (doorCounts.length == 1) doorCount = doorCounts[0];
  }

  return doorCount;
}

export function selectedFeatures({ features, item, configOptions, components }) {
  const tally = {};
  features.forEach((feature) => {
    tally[feature.id] = {
      id: feature.id,
      title: feature.title,
      qty: 0
    };
  });

  if (!strategyFor(item.productId).usesConfigFeatures) return tally;

  item.specs.sections.forEach((section) => {
    const config = findById(configOptions, section.configId);
    if (!config) return;
    const parsed = parseComponents(config.components);
    if (_.isNull(parsed) || !_.isObject(parsed)) return;
    for (const [compId, compQty] of Object.entries(parsed)) {
      const qty = parseFloat(compQty);
      if (!qty) continue;
      const component = components.find((obj) => obj.id == parseFloat(compId));
      if (component && component.features) {
        component.features.forEach((feature) => {
          tally[feature.id].qty += qty;
        });
      }
    }
  });

  return tally;
}

export function currentLayout(item, productLayouts) {
  if (_.isUndefined(item) || !item.productLayout || _.isEmpty(productLayouts)) return null;
  return findById(productLayouts, item.productLayout.id) ?? null;
}

export function layoutSectionTitle(item, productLayouts, sectionIndex) {
  const layout = currentLayout(item, productLayouts);
  if (layout && layout.sections[sectionIndex]) return layout.sections[sectionIndex].title;
  return '';
}

export function layoutSectionTitles(item, productLayouts) {
  const layout = currentLayout(item, productLayouts);
  if (!layout) return [];
  return layout.sections.map((section) => section.title);
}

export function pageTitleHtml(product, subTitle) {
  if (!product) return '';
  let string = '<strong>' + product.title;
  if (subTitle) string += ':';
  string += '</strong>';
  if (subTitle) string += ' ' + subTitle;
  return string;
}

export function canAddMoreItems(items, itemsMax, sectionsMax, rangeHandle) {
  const itemsTotal = items.length;
  let sectionsTotal = 0;
  items.forEach((item) => {
    if (!_.isUndefined(item.specs.sections) && _.isArray(item.specs.sections)) {
      item.specs.sections.forEach(() => {
        sectionsTotal++;
      });
    }
  });
  if (itemsTotal >= itemsMax || sectionsTotal >= sectionsMax - 3) return false;
  if (rangeHandle !== 'wardrobes') return false;
  return true;
}

export function isLegacyQuote(quote, items) {
  let legacy = false;
  if (!_.isUndefined(quote) && quote.builderVersion < 2) legacy = true;
  if (!_.isUndefined(quote) && !quote.ordered) {
    items.forEach((item) => {
      if (!_.isUndefined(item.productId) && strategyFor(item.productId).isLegacyProduct) {
        legacy = true;
      }
    });
  }
  return legacy;
}

export function productHandleLabel(product) {
  if (!_.isNull(product) && !_.isUndefined(product.handle)) {
    const productHandle = product.handle;
    if (productHandle.length) return productHandle.charAt(0).toUpperCase() + productHandle.slice(1);
  }
  return '';
}

export function productTitleForId(products, productId) {
  if (_.isUndefined(products)) return '';
  const product = findById(products, productId);
  return product ? product.title : '';
}

export function parseProductStructure(product) {
  if (!_.isNull(product) && !_.isUndefined(product.structure)) {
    return JSON.parse(product.structure);
  }
  return null;
}

export function parseStepsStructure(product) {
  if (product) {
    return JSON.parse(product.stepsStructure);
  }
  return [
    {
      number: 1,
      title: 'Type and Layout',
      nextText: 'Start Building',
      saveText: 'Save and exit'
    }
  ];
}

export function stepWithSectionsNumber(steps) {
  for (let i = 0; i < steps.length; i++) {
    if (!_.isUndefined(steps[i].hasSections) && steps[i].hasSections) {
      return i + 1;
    }
  }
  return null;
}

export function sectionCount(item) {
  if (!_.isUndefined(item) && item.productLayout) {
    return item.productLayout.sectionsCount;
  }
  return 1;
}
